import { Suspense, lazy, useCallback, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { DESIGN_CARD_MEDIA_ENTITY_TYPE, type DesignCard } from '@tailoring-crm/shared';
import { useApiClient } from '../../lib/api/api-context.js';
import {
  useDeleteDesignSketch,
  useDesignSketch,
  useSaveDesignSketch,
} from '../../lib/design-board/use-design-board.js';
import { uploadSketchRendering } from '../../lib/design-board/sketch-export.js';
import type { SketchSnapshot } from './design-sketch-canvas.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useConfirm,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';

/**
 * DRAWING ON A CARD — the neckline that exists in nobody's photograph yet.
 *
 * The card board settles what was agreed; it cannot settle what is still being argued, because
 * there is no picture of a garment nobody has made. This is where that argument happens, and it
 * is deliberately not a second kind of record: the drawing hangs on a card, wears that card's
 * title and status, and travels with it.
 *
 * The canvas itself is loaded lazily and lives next door in `design-sketch-canvas.tsx` — see the
 * note there about why nothing else may import the drawing library.
 */

const DesignSketchCanvas = lazy(() => import('./design-sketch-canvas.js'));

export interface DesignSketchDialogProps {
  card: DesignCard;
  /** Somebody else's work: the drawing opens to be read, not edited. */
  readOnly?: boolean;
  onClose: () => void;
}

export function DesignSketchDialog({
  card,
  readOnly = false,
  onClose,
}: DesignSketchDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const client = useApiClient();
  const confirm = useConfirm();

  const sketch = useDesignSketch(card.id);
  const save = useSaveDesignSketch();
  const remove = useDeleteDesignSketch();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The canvas's way of being asked for its contents. A ref and not state: it is set once when
   * the editor mounts and reading it must never be a reason to re-render a drawing surface.
   */
  const takeSnapshot = useRef<(() => Promise<SketchSnapshot>) | null>(null);
  const bindCanvas = useCallback((take: () => Promise<SketchSnapshot>) => {
    takeSnapshot.current = take;
  }, []);

  /**
   * Save: serialise, try to render, then write.
   *
   * THE ORDER MATTERS AND THE FAILURE MODES ARE NOT SYMMETRICAL. The scene is what cannot be
   * regenerated, so the upload of its picture is allowed to fail without taking the save with
   * it — the card simply keeps whatever thumbnail it had until the next save succeeds. A failure
   * of the save itself is a real error and is reported as one.
   */
  const submit = async (): Promise<void> => {
    const take = takeSnapshot.current;
    if (!take) return;

    setSaving(true);
    setError(null);
    try {
      const snapshot = await take();

      let previewMediaId: string | null = null;
      if (snapshot.png) {
        try {
          previewMediaId = await uploadSketchRendering({
            client,
            entityType: DESIGN_CARD_MEDIA_ENTITY_TYPE,
            // The card already exists — a drawing is made ON one — so the image lands attached.
            entityId: card.id,
            png: snapshot.png,
            title: card.title,
          });
        } catch {
          // Deliberately swallowed — see above. The drawing is about to be saved regardless, and
          // a toast about a thumbnail would read as "your work was not saved", which is false.
          previewMediaId = null;
        }
      }

      await save.mutateAsync({
        cardId: card.id,
        input: { scene: snapshot.scene, previewMediaId },
      });

      toast({ title: t('designBoard.sketch.saved') });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    } finally {
      setSaving(false);
    }
  };

  const askDelete = async (): Promise<void> => {
    const confirmed = await confirm({
      title: t('designBoard.sketch.delete.title', { name: card.title }),
      description: t('designBoard.sketch.delete.description'),
      confirmLabel: t('designBoard.sketch.delete.confirm'),
      destructive: true,
    });
    if (!confirmed) return;
    await remove.mutateAsync(card.id);
    toast({ title: t('designBoard.sketch.delete.done') });
    onClose();
  };

  /*
   * The scene is fetched before the canvas mounts, never alongside it. The editor reads its
   * starting drawing exactly once, at mount, so handing it a null scene that arrives a moment
   * later would open a blank sheet over an existing drawing — and the first Save would then
   * write that blank sheet over it.
   */
  const loading = sketch.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        /* Nearly the whole viewport: a drawing surface the size of a form field is not one. */
        className="flex h-[92vh] max-h-[92vh] w-[96vw] max-w-[96vw] flex-col gap-3 overflow-hidden sm:max-w-[96vw]"
        /*
         * Escape and a press outside do NOT close a drawing. Escape is how the canvas finishes a
         * text label or drops a selection, and both used to throw the whole unsaved drawing away.
         * Cancel and the close button are the ways out.
         */
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('designBoard.sketch.title', { name: card.title })}</DialogTitle>
        </DialogHeader>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}

        {sketch.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('designBoard.sketch.loadFailed')}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border">
          {loading || sketch.isError ? (
            <div className="p-4">
              <ListSkeleton rows={6} />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="p-4">
                  <ListSkeleton rows={6} />
                </div>
              }
            >
              <DesignSketchCanvas
                initialScene={sketch.data?.scene ?? null}
                readOnly={readOnly}
                onReady={bindCanvas}
              />
            </Suspense>
          )}
        </div>

        {/* Not `DialogFooter`: the destructive action sits apart from the pair on the right,
            because "throw the drawing away" and "keep it" should not be neighbours. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {!readOnly && sketch.data ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                disabled={remove.isPending || saving}
                onClick={() => void askDelete()}
              >
                <Trash2 aria-hidden="true" />
                {t('designBoard.sketch.delete.action')}
              </Button>
            ) : null}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {readOnly ? t('common.close') : t('common.cancel')}
            </Button>
            {readOnly ? null : (
              <Button type="button" disabled={loading || saving} onClick={() => void submit()}>
                {saving ? t('designBoard.sketch.saving') : t('common.save')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
